// Coming back from "Continue with Google": the server redirects to the page the
// user started on with `?auth=google_signup|google_login` or `?auth_error=<code>`.
// `?auth=forgot` (from an expired reset link) opens "Forgot your password?".
// Read it once, drop it from the address bar, and report what happened.

import { track } from '../analytics';

const ERRORS: Record<string, string> = {
  google_link: 'An account with this email already exists. Sign in with your password.',
  google_unverified: 'Google has not verified this email address, so it can’t be used to sign in.',
  google_failed: 'Signing in with Google didn’t work. Please try again.',
};

export type AuthReturn = { signedIn: 'signup' | 'login' } | { error: string } | { forgot: true } | null;

export function consumeAuthReturn(): AuthReturn {
  const url = new URL(location.href);
  const auth = url.searchParams.get('auth');
  const err = url.searchParams.get('auth_error');
  if (auth === null && err === null) return null;
  url.searchParams.delete('auth');
  url.searchParams.delete('auth_error');
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  if (auth === 'google_signup' || auth === 'google_login') {
    const signedIn = auth === 'google_signup' ? 'signup' : 'login';
    track(signedIn === 'signup' ? 'sign_up' : 'login', { method: 'google' });
    return { signedIn };
  }
  // From an expired password-reset link: "Request a new link".
  if (auth === 'forgot') return { forgot: true };
  return err !== null ? { error: ERRORS[err] ?? ERRORS.google_failed } : null;
}

/** Where to send the browser to sign in with Google, coming back to this page. */
export function googleSignInUrl(returnTo: string): string {
  return `/api/auth/google?return=${encodeURIComponent(returnTo)}`;
}
